// calendar_event → an RFC 5545 .ics file. The platform-neutral equivalent of the
// native calendar insert: the user downloads the event and their calendar app
// imports it — nothing is written anywhere unprompted. A stable UID plus
// SEQUENCE makes a re-download an update, not a duplicate.

import type { CalendarEventElement } from '$lib/protocol/types';

function icsDate(date: Date): string {
  return date
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z');
}

function escapeText(text: string): string {
  return text
    .replaceAll('\\', '\\\\')
    .replaceAll(';', '\\;')
    .replaceAll(',', '\\,')
    .replaceAll('\r\n', '\\n')
    .replaceAll('\n', '\\n');
}

/** Folds lines at 74 octets-ish (RFC 5545 §3.1) — simple char-based fold is fine
 *  for our short fields. */
function fold(line: string): string {
  if (line.length <= 74) {
    return line;
  }
  const parts: string[] = [];
  let rest = line;
  parts.push(rest.slice(0, 74));
  rest = rest.slice(74);
  while (rest.length > 0) {
    parts.push(` ${rest.slice(0, 73)}`);
    rest = rest.slice(73);
  }
  return parts.join('\r\n');
}

export function calendarEventIcs(
  element: CalendarEventElement,
  context: { accountId: string; sequence: number },
): string {
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Dialano//webclient//EN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${escapeText(context.accountId)}-${escapeText(element.eventId)}@dialano`,
    `SEQUENCE:${context.sequence}`,
    `DTSTAMP:${icsDate(new Date())}`,
    `DTSTART:${icsDate(element.startsAt)}`,
  ];
  if (element.endsAt !== undefined) {
    lines.push(`DTEND:${icsDate(element.endsAt)}`);
  }
  lines.push(`SUMMARY:${escapeText(element.title)}`);
  if (element.place !== undefined) {
    lines.push(`LOCATION:${escapeText(element.place)}`);
  }
  if (element.note !== undefined) {
    lines.push(`DESCRIPTION:${escapeText(element.note)}`);
  }
  lines.push(`STATUS:${element.status === 'cancelled' ? 'CANCELLED' : 'CONFIRMED'}`);
  lines.push('END:VEVENT', 'END:VCALENDAR');
  return lines.map(fold).join('\r\n') + '\r\n';
}
